'use strict';

import Swiper from 'swiper';
import { Pagination } from 'swiper/modules';
import { initialiseVideo } from './header-video';

function createImagePlayer(duration) {
    let startTime = 0;
    let elapsed = 0;
    let timer = null;
    let _paused = true;
    const events = {};

    // helper to emit events
    const _emit = (event, data) => {
        (events[event] || []).forEach(cb => cb(data));
    };

    const tick = () => {
        if (_paused) {
            return;
        }

        const delta = Date.now() - startTime;
        const percent = Math.min(((elapsed + delta) / (duration * 1000)) * 100, 100);

        _emit('progress', percent);

        if (percent < 100) {
            requestAnimationFrame(tick);
        } else {
            _emit('ended');
        }
    }

    return {
        async play() {
            if (!_paused) {
                return;
            }

            _paused = false;
            startTime = Date.now();
            timer = setTimeout(() => _emit('ended'), (duration * 1000) - elapsed);
            tick();
            _emit('play');
        },

        async pause() {
            if (_paused) return;
            _paused = true;
            elapsed += Date.now() - startTime;
            clearTimeout(timer);
            _emit('pause');
        },

        async stop() {
            await this.pause();
            elapsed = 0;
        },

        async reset() {
            elapsed = 0;
            startTime = 0;
            clearTimeout(timer);
            _paused = true;
        },

        async duration() {
            return duration;
        },

        async isPlaying() {
            return !_paused;
        },

        on(event, callback) {
            (events[event] ||= []).push(callback);
        },

        off(event, callback) {
            if (!events[event]) {
                return;
            }

            events[event] = events[event].filter(cb => cb !== callback);
        },
    }
}

export function initialiseElement(element) {
    const scrollerElement = element.querySelector('.swiper-container');
    const paginationElement = element.querySelector('.swiper-pagination');
    const progressCircle = document.querySelector(".autoplay-progress__foreground");

    const imageDuration = parseInt(element.dataset.swiperDelay, 10) || 4;

    const totalSlides = scrollerElement.querySelectorAll('.swiper-slide').length;

    const swiperInstance = new Swiper(scrollerElement, {
        slidesPerView: '1',
        grabCursor: false,
        effect: 'slide',
        speed: 1000,
        loop: false,
        pagination: {
            el: paginationElement,
            type: 'bullets',
            bulletElement: 'button',
            clickable: true,
            renderBullet(index, className) {
                return `<button class="${className}"><span class="visually-hidden">Slide ${index + 1} of ${totalSlides}</span></button>`;
            },
        },
        on: {
            init(s) {
                for (const slide of s.slides) {
                    if (slide.classList.contains('home-hero-item--video')) {
                        slide._loaded = initialiseVideo(slide)?.then(player => {
                            if (player) {
                                slide._player = player;
                            }

                            return true;
                        });
                    } else {
                        slide._loaded = new Promise(resolve => {
                            slide._player = createImagePlayer(imageDuration);

                            resolve(true);
                        });
                    }
                }
            },
            transitionStart: function () {
                this.el.classList.add('is-animating');
            },
            transitionEnd: function () {
                this.el.classList.remove('is-animating');
            }
        },
        autoplay: false,
        modules: [Pagination],
    });

    const playButtons = [ ...element.querySelectorAll('.home-hero__stop-autoplay') ];

    const toggleButtonState = isPlaying => {
        for (const playButton of playButtons) {
            if (isPlaying) {
                playButton.querySelector('.icon').innerHTML = '<svg class="o-icon o-icon--pause"><use xlink:href="#pause"></use></svg>';
                playButton.querySelector('.label').textContent = 'Pause';
            } else {
                playButton.querySelector('.icon').innerHTML = '<svg class="o-icon o-icon--play"><use xlink:href="#play"></use></svg>';
                playButton.querySelector('.label').textContent = 'Play';
            }
        }
    }

    let paused = false;

    function setProgress(percent) {
        progressCircle?.style.setProperty('--progress', percent);
    }

    async function startAutoplay() {
        const slide = swiperInstance.slides[swiperInstance.activeIndex];

        await slide._loaded;

        slide._player?.reset();

        toggleButtonState(true);

        slide._player?._cleanup?.();

        const onProgress = percent => setProgress(percent);

        const onEnded = () => {
            cleanup();

            if (swiperInstance.isEnd) {
                if (swiperInstance.activeIndex === 0) {
                    startAutoplay();
                } else {
                    swiperInstance.slideTo(0);
                }
            } else {
                swiperInstance.slideNext();
            }
        }

        slide._player?.on('progress', onProgress);
        slide._player?.on('ended', onEnded);

        slide._player?.play();

        function cleanup() {
            slide._player?.off('progress', onProgress);
            slide._player?.off('ended', onEnded);
        }

        if (slide._player) {
            slide._player._cleanup = cleanup;
        }
    }

    swiperInstance.on('slideChangeTransitionStart', () => {
        const prev = swiperInstance.slides[swiperInstance.previousIndex];

        if (!prev) {
            return;
        }

        prev._player?._cleanup?.();
        prev._player?.pause();
        prev._player?.reset();
        setProgress(0);
    });

    swiperInstance.on('slideChange', () => {
        if (!paused) {
            startAutoplay();
        }
    });

    async function pause() {
        if (paused) {
            return;
        }

        paused = true;

        const slide = swiperInstance.slides[swiperInstance.activeIndex];
        await slide._loaded;

        slide._player?.pause();
    }

    async function resume() {
        if (!paused) {
            return;
        }

        paused = false;

        const slide = swiperInstance.slides[swiperInstance.activeIndex];
        await slide._loaded;

        if (!slide._player?._cleanup) {
            return startAutoplay();
        }

        slide._player?.play();
    }

    for (const playButton of playButtons) {
        playButton.addEventListener('click', () => {
            if (paused) {
                resume();
                toggleButtonState(true);
            } else {
                pause();
                toggleButtonState(false);
            }
        });
    }

    if (element.classList.contains('home-hero--multiple') || swiperInstance.slides[0]?.classList.contains('home-hero-item--video')) {
        startAutoplay();
    }
}

export default function () {
    document.querySelectorAll('.js-home-hero').forEach(initialiseElement);
}
